/**
 * IntroEmailSection (0161) — the "Copy intro email" block on the People-tab
 * bio/photo editor (owner path AND the manage_staff_profiles editor).
 *
 * One click writes a fully formatted email body to the clipboard
 * (ClipboardItem text/html + text/plain fallback — copyRichToClipboard)
 * that pastes cleanly into Outlook: the CM's name + credentials, photo
 * (inlined as a data URI so Outlook embeds it), bio paragraphs, and direct
 * contact lines, signed by the signed-in unit lead. The parts the unit lead
 * fills by hand ([Client first name] ×2, [Timing note], and any profile
 * fact not on file) are highlighted in BOTH the on-page preview and the
 * copied HTML — src/lib/introEmail.ts owns the template + rendering.
 *
 * Template wording comes through the D2 config seam
 * (config.features.cm_intro_email → readIntroEmailParagraphs) with the
 * platform fallback; the profile facts come in as props from the People-tab
 * rows. The preview HTML is generated locally with every interpolated value
 * escaped (introEmail.ts), so rendering it via dangerouslySetInnerHTML does
 * not execute operator-entered content.
 */

import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { Button } from '@/components/primitives/Button';
import { useTenant } from '@/lib/auth';
import { useTenantConfig } from '@/hooks/useTenantConfig';
import { cmPhotoUrl } from '@/lib/staffAdmin';
import { readIntroEmailParagraphs, renderIntroEmail } from '@/lib/introEmail';
import { copyIntroEmail, fetchCmPhotoDataUri } from '@/lib/introEmailCopy';
import { roleNoun } from '@/lib/people';

export function IntroEmailSection({
  tenantSlug,
  rosterId,
  fullName,
  credentials,
  preferredName,
  bio,
  email,
  phone,
  doesCareManagement,
  doesSpice,
  hasPhoto,
  version,
}: {
  tenantSlug: string;
  rosterId: string;
  fullName: string;
  credentials: string | null;
  preferredName: string | null;
  bio: string | null;
  email: string | null;
  phone: string | null;
  /** The capability flags decide what the email calls the person (roleNoun):
   *  a SPICE-only therapist is never introduced as the care manager. */
  doesCareManagement: boolean;
  doesSpice: boolean;
  hasPhoto: boolean;
  /** Photo cache-bust token (bumped on upload/remove) — a replace reaches
   *  the copied email immediately, not after the browser cache expires. */
  version?: number;
}): ReactNode {
  const { whoami } = useTenant();
  const tenantConfig = useTenantConfig(tenantSlug, { enabled: tenantSlug.length > 0 });
  const paragraphs = readIntroEmailParagraphs(tenantConfig.data?.config);
  const role = roleNoun({ does_care_management: doesCareManagement, does_spice: doesSpice });

  const [open, setOpen] = useState(false);
  // undefined = not fetched yet; null = no photo / fetch failed.
  const [photoUri, setPhotoUri] = useState<string | null | undefined>(
    hasPhoto ? undefined : null,
  );
  const [copied, setCopied] = useState(false);

  // A photo upload/remove invalidates the cached data URI.
  useEffect(() => {
    setPhotoUri(hasPhoto ? undefined : null);
  }, [hasPhoto, version]);

  useEffect(() => {
    if (copied === false) return;
    const handle = setTimeout(() => setCopied(false), 4000);
    return () => clearTimeout(handle);
  }, [copied]);

  // Load the photo for the preview as soon as it opens.
  useEffect(() => {
    if (!open || photoUri !== undefined) return;
    let cancelled = false;
    void fetchCmPhotoDataUri(cmPhotoUrl(tenantSlug, rosterId, version)).then((uri) => {
      if (!cancelled) setPhotoUri(uri);
    });
    return () => {
      cancelled = true;
    };
  }, [open, photoUri, tenantSlug, rosterId, version]);

  const rendered = useMemo(
    () =>
      renderIntroEmail(
        {
          full_name: fullName,
          credentials,
          preferred_name: preferredName,
          bio,
          email,
          phone,
          photo_data_uri: photoUri ?? null,
          sender_name: whoami?.display_name ?? null,
          role_noun: role,
        },
        paragraphs,
      ),
    [fullName, credentials, preferredName, bio, email, phone, photoUri, whoami, paragraphs, role],
  );

  const onCopy = async (): Promise<void> => {
    // Resolve the photo before the first copy if the preview never opened.
    let uri = photoUri;
    if (uri === undefined) {
      uri = await fetchCmPhotoDataUri(cmPhotoUrl(tenantSlug, rosterId, version));
      setPhotoUri(uri);
    }
    // Shared with the drawer's Care manager row (introEmailCopy.ts) so the
    // two surfaces cannot drift apart on template or clipboard behavior.
    await copyIntroEmail(
      {
        full_name: fullName,
        credentials,
        preferred_name: preferredName,
        bio,
        email,
        phone,
        photo_data_uri: uri,
        sender_name: whoami?.display_name ?? null,
        role_noun: role,
      },
      paragraphs,
    );
    setCopied(true);
  };

  return (
    <div className="flex flex-col gap-2" data-testid={`intro-email-${rosterId}`}>
      <div className="flex flex-wrap items-center gap-2">
        <Button
          variant="secondary"
          size="sm"
          onClick={() => void onCopy()}
          data-testid={`intro-email-copy-${rosterId}`}
        >
          Copy intro email
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setOpen((v) => !v)}
          data-testid={`intro-email-toggle-${rosterId}`}
          aria-expanded={open}
        >
          {/* Deliberately NOT a bare "Preview": the Team tab's bulk-add flow
              owns the anchored /^Preview$/ e2e locator, and this button
              renders once per staff row on the same page — a bare label
              would make that locator resolve to N elements (strict-mode
              violation). Keep the label distinct. */}
          {open ? 'Hide intro email preview' : 'Preview intro email'}
        </Button>
        {copied && (
          <span role="status" aria-live="polite" className="text-xs text-sage-700">
            Copied. Paste into the Outlook email body.
          </span>
        )}
      </div>
      {open && (
        <div className="flex flex-col gap-1.5">
          <p className="text-xs text-ink-soft">
            The highlighted parts are yours to fill in Outlook: the client&apos;s first name and
            a timing note{phone === null ? ', plus the phone number not on file yet' : ''}.
          </p>
          <div
            data-testid={`intro-email-preview-${rosterId}`}
            className="border border-sage-800/[12%] rounded-md p-3 bg-paper text-sm overflow-x-auto"
            // Locally generated HTML; every interpolated value is escaped in
            // introEmail.ts (never raw operator/user input).
            dangerouslySetInnerHTML={{ __html: rendered.html }}
          />
        </div>
      )}
    </div>
  );
}
